/**
 * Trusted Query Parser
 * Parses plain text, shorthand tokens, JSON, and markdown-style input into a canonical search intent.
 * Maps intent to trusted-search API params and display filters.
 */

// --- Canonical Search Intent Schema ---
// Single source of truth for search state. All parsers produce this shape.

/** @typedef {'text'|'shorthand'|'json'|'markdown'|'invalid'} ParseMode */

/** @typedef {Object} SearchIntent
 * @property {string} query - Search keyword (required for search)
 * @property {number|null} minPrice
 * @property {number|null} maxPrice
 * @property {string} listingType - 'FIXED_PRICE'|'AUCTION'|'BOTH'
 * @property {string} condition - 'all'|'New'|'Refurb'|'Used'
 * @property {boolean} returnsAccepted
 * @property {boolean} freeShipping
 * @property {number} minQuantity - minimum quantity in stock (default 1)
 * @property {boolean} includeForParts - include "For Parts or not working" listings
 * @property {string} trustedLevel - 'off'|'standard'|'strict'|'elite'
 * @property {number|null} minSellerFeedbackPct - custom override when not preset
 * @property {number|null} minSellerReviews - custom override when not preset
 * @property {string} sortOrder
 * @property {string|null} categoryId
 * @property {number} pageSize
 */

/** @typedef {Object} ParseWarning
 * @property {string} code - e.g. 'unknown_token', 'invalid_range'
 * @property {string} message
 * @property {boolean} [blocking] - if true, search should not proceed
 */

/** @typedef {Object} ParseResult
 * @property {SearchIntent} intent
 * @property {ParseMode} mode
 * @property {ParseWarning[]} warnings
 * @property {string|null} error - blocking error message if mode is 'invalid'
 */

const VALID_SORT_ORDERS = ['priceAscending', 'priceDescending', 'sellerScoreDescending', 'newlyListed', 'bestMatch'];
const VALID_LISTING_TYPES = ['FIXED_PRICE', 'AUCTION', 'BOTH'];
const VALID_CONDITIONS = ['all', 'New', 'Refurb', 'Used'];
/*
 * The four trust floors, loosest first — the same four floor-state.js carries
 * and the same four the pane and the top bar offer. `off` is here because the
 * floor is global state and one of its four values is "no floor": without an
 * entry, choosing Off on this page fell through to `|| TRUSTED_PRESETS.strict`
 * below and quietly ran a Strict search under a control that said Off.
 * Zero/zero is what "all sellers" means to the eBay filter.
 */
const VALID_TRUSTED_LEVELS = ['off', 'standard', 'strict', 'elite'];

const TRUSTED_PRESETS = {
  off: { feedback: 0, reviews: 0 },
  standard: { feedback: 99.0, reviews: 50 },
  strict: { feedback: 99.5, reviews: 50 },
  elite: { feedback: 99.8, reviews: 100 }
};

const DEFAULT_INTENT = Object.freeze({
  query: '',
  minPrice: null,
  maxPrice: null,
  listingType: 'FIXED_PRICE',
  condition: 'all',
  returnsAccepted: true,
  freeShipping: false,
  minQuantity: 1,
  includeForParts: false,
  trustedLevel: 'strict',
  minSellerFeedbackPct: null,
  minSellerReviews: null,
  sortOrder: 'priceAscending',
  categoryId: null,
  pageSize: 20
});

const SORT_ALIASES = {
  price: 'priceAscending',
  price_asc: 'priceAscending',
  price_ascending: 'priceAscending',
  low: 'priceAscending',
  price_desc: 'priceDescending',
  price_descending: 'priceDescending',
  high: 'priceDescending',
  seller: 'sellerScoreDescending',
  seller_score: 'sellerScoreDescending',
  newest: 'newlyListed',
  new: 'newlyListed',
  relevance: 'bestMatch',
  best: 'bestMatch'
};

const CONDITION_ALIASES = {
  new: 'New',
  used: 'Used',
  refurb: 'Refurb',
  refurbished: 'Refurb',
  // Open box is a new-family condition here and everywhere else on the site
  // (SavedSearch.CONDITION_FAMILY_CHOICES, products/market_key.py). The Console
  // Feed's first worked example is `airpods pro 2 condition:openbox`, and
  // without these it parsed to "all" -- the page advertising a syntax that
  // quietly did nothing.
  openbox: 'New',
  open_box: 'New',
  'open-box': 'New',
  all: 'all'
};

const LISTING_ALIASES = {
  fixed: 'FIXED_PRICE',
  fixed_price: 'FIXED_PRICE',
  buy_it_now: 'FIXED_PRICE',
  auction: 'AUCTION',
  both: 'BOTH',
  all: 'BOTH'
};

const SYNTAX_HELP = [
  { token: '$min-$max', desc: 'Price range', example: '$50-$200' },
  { token: 'condition:new|refurb|used|all', desc: 'Item condition', example: 'condition:new' },
  { token: 'listing:fixed|auction|both', desc: 'Listing type', example: 'listing:auction' },
  { token: 'sort:price|seller|newest', desc: 'Sort order', example: 'sort:price' },
  { token: 'trusted:standard|strict|elite', desc: 'Seller trust level', example: 'trusted:strict' },
  { token: 'returns:yes|no', desc: 'Returns accepted only', example: 'returns:yes' },
  { token: 'shipping:free|any', desc: 'Free shipping only', example: 'shipping:free' },
  { token: 'qty:N', desc: 'Minimum quantity in stock', example: 'qty:2' },
  { token: 'forparts:include|exclude', desc: 'Include For Parts or not working', example: 'forparts:include' },
  { token: 'category:id', desc: 'eBay category ID', example: 'category:9355' },
  { token: 'limit:N', desc: 'Items per page', example: 'limit:50' }
];

const EXAMPLES = [
  'bose quietcomfort ultra $150-$350 condition:new',
  'dyson airwrap returns:yes shipping:free',
  'iphone 16 pro max $800-$1200 condition:used qty:2'
];

/**
 * Detect parse mode from raw input.
 * @param {string} input
 * @returns {ParseMode}
 */
export function detectMode(input) {
  if (!input || typeof input !== 'string') return 'text';
  const trimmed = input.trim();
  if (!trimmed) return 'text';

  // JSON: starts with { and ends with } (allow whitespace)
  const jsonLike = trimmed.replace(/^\s+/, '').replace(/\s+$/, '');
  if (jsonLike.startsWith('{') && jsonLike.endsWith('}')) {
    try {
      JSON.parse(jsonLike);
      return 'json';
    } catch {
      return 'invalid';
    }
  }

  // Markdown-style: lines like "key: value" (at least one)
  const lines = trimmed.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  if (lines.length >= 1) {
    const kvCount = lines.filter(l => /^[a-zA-Z_][a-zA-Z0-9_]*\s*:\s*/.test(l)).length;
    if (kvCount >= 1 && kvCount === lines.length) return 'markdown';
    if (kvCount >= 1) return 'shorthand'; // mixed
  }

  // Shorthand: contains token patterns
  const shorthandPatterns = [
    /\$\d+(?:\.\d+)?(?:-\$\d+(?:\.\d+)?)?/,
    /<(?:=)?\s*\$\d+/,
    />\s*\$\d+/,
    /condition\s*:\s*\w+/i,
    /listing\s*:\s*\w+/i,
    /sort\s*:\s*\w+/i,
    /trusted\s*:\s*\w+/i,
    /returns\s*:\s*(?:yes|no)/i,
    /shipping\s*:\s*(?:free|any)/i,
    /qty\s*:\s*\d+/i,
    /forparts\s*:\s*(?:include|exclude)/i,
    /category\s*:\s*\S+/i,
    /limit\s*:\s*\d+/i
  ];
  if (shorthandPatterns.some(p => p.test(trimmed))) return 'shorthand';

  return 'text';
}

/**
 * Parse shorthand tokens from input, return { keyword, filters, warnings }.
 * @param {string} input
 * @returns {{ keyword: string, filters: Partial<SearchIntent>, warnings: ParseWarning[] }}
 */
function parseShorthand(input) {
  let text = input.trim();
  const filters = {};
  const shorthandWarnings = [];

  // Price: $min-$max, $min-max, <$max, >$min
  //
  // The second dollar sign is optional. Every example the Console shows —
  // the Feed hero, its worked queries, the command bar's `$min-max` hint —
  // writes the range as `$900-1300`, which is also what people type. Requiring
  // `$900-$1300` meant the advertised syntax parsed as part of the keyword and
  // the search silently returned nothing.
  const priceRange = text.match(/\$(\d+(?:\.\d+)?)\s*-\s*\$?(\d+(?:\.\d+)?)/);
  if (priceRange) {
    filters.minPrice = parseFloat(priceRange[1]);
    filters.maxPrice = parseFloat(priceRange[2]);
    text = text.replace(priceRange[0], '').trim();
  } else {
    const maxOnly = text.match(/<\s*\$?(\d+(?:\.\d+)?)/);
    if (maxOnly) {
      filters.maxPrice = parseFloat(maxOnly[1]);
      text = text.replace(maxOnly[0], '').trim();
    }
    const minOnly = text.match(/>\s*\$?(\d+(?:\.\d+)?)/);
    if (minOnly) {
      filters.minPrice = parseFloat(minOnly[1]);
      text = text.replace(minOnly[0], '').trim();
    }
  }

  // condition:new|openbox|refurb|used|all
  const condMatch = text.match(/condition\s*:\s*([\w-]+)/i);
  if (condMatch) {
    const v = (condMatch[1] || '').toLowerCase();
    const mapped = CONDITION_ALIASES[v];
    if (mapped) {
      filters.condition = mapped;
    } else {
      // Falling back to "all" silently is how `condition:openbox` came to be
      // advertised for months while doing nothing. Say so instead; the caller
      // decides whether to show it.
      shorthandWarnings.push({
        code: 'unknown_condition',
        message: `Unknown condition “${condMatch[1]}” — ignored`,
        blocking: false,
      });
    }
    text = text.replace(condMatch[0], '').trim();
  }

  // listing:fixed|auction|both
  const listMatch = text.match(/listing\s*:\s*(\w+)/i);
  if (listMatch) {
    const v = (listMatch[1] || '').toLowerCase().replace(/-/g, '_');
    filters.listingType = LISTING_ALIASES[v] ?? 'FIXED_PRICE';
    text = text.replace(listMatch[0], '').trim();
  }

  // sort:price|seller|newest|...
  const sortMatch = text.match(/sort\s*:\s*(\w+)/i);
  if (sortMatch) {
    const v = (sortMatch[1] || '').toLowerCase().replace(/-/g, '_');
    filters.sortOrder = SORT_ALIASES[v] ?? 'priceAscending';
    if (!VALID_SORT_ORDERS.includes(filters.sortOrder)) filters.sortOrder = 'priceAscending';
    text = text.replace(sortMatch[0], '').trim();
  }

  // trusted:standard|strict|elite
  const trustMatch = text.match(/trusted\s*:\s*(\w+)/i);
  if (trustMatch) {
    const v = (trustMatch[1] || '').toLowerCase();
    filters.trustedLevel = VALID_TRUSTED_LEVELS.includes(v) ? v : 'strict';
    text = text.replace(trustMatch[0], '').trim();
  }

  // returns:yes|no (returns always required; no is ignored)
  const retMatch = text.match(/returns\s*:\s*(yes|no)/i);
  if (retMatch) {
    filters.returnsAccepted = true;
    text = text.replace(retMatch[0], '').trim();
  }

  // category:id
  const catMatch = text.match(/category\s*:\s*(\S+)/i);
  if (catMatch) {
    filters.categoryId = (catMatch[1] || '').trim();
    text = text.replace(catMatch[0], '').trim();
  }

  // shipping:free|any
  const shipMatch = text.match(/shipping\s*:\s*(free|any)/i);
  if (shipMatch) {
    filters.freeShipping = (shipMatch[1] || '').toLowerCase() === 'free';
    text = text.replace(shipMatch[0], '').trim();
  }

  // qty:N
  const qtyMatch = text.match(/qty\s*:\s*(\d+)/i);
  if (qtyMatch) {
    const n = parseInt(qtyMatch[1], 10);
    filters.minQuantity = Math.min(999, Math.max(1, n));
    text = text.replace(qtyMatch[0], '').trim();
  }

  // forparts:include|exclude
  const forpartsMatch = text.match(/forparts\s*:\s*(include|exclude)/i);
  if (forpartsMatch) {
    filters.includeForParts = (forpartsMatch[1] || '').toLowerCase() === 'include';
    text = text.replace(forpartsMatch[0], '').trim();
  }

  // limit:N
  const limitMatch = text.match(/limit\s*:\s*(\d+)/i);
  if (limitMatch) {
    const n = parseInt(limitMatch[1], 10);
    filters.pageSize = Math.min(200, Math.max(1, n));
    text = text.replace(limitMatch[0], '').trim();
  }

  const keyword = text.replace(/\s+/g, ' ').trim();
  return { keyword, filters, warnings: shorthandWarnings };
}

/**
 * Parse JSON input into intent fields.
 * @param {string} input
 * @returns {{ intent: Partial<SearchIntent>, error?: string }}
 */
function parseJsonInput(input) {
  try {
    const obj = JSON.parse(input.trim());
    const intent = { ...DEFAULT_INTENT };

    if (typeof obj.query === 'string') intent.query = obj.query.trim();
    else if (typeof obj.q === 'string') intent.query = obj.q.trim();

    if (obj.price) {
      if (typeof obj.price.min === 'number' && obj.price.min >= 0) intent.minPrice = obj.price.min;
      if (typeof obj.price.max === 'number' && obj.price.max >= 0) intent.maxPrice = obj.price.max;
    }
    if (typeof obj.min_price === 'number' && obj.min_price >= 0) intent.minPrice = obj.min_price;
    if (typeof obj.max_price === 'number' && obj.max_price >= 0) intent.maxPrice = obj.max_price;
    if (typeof obj.minPrice === 'number' && obj.minPrice >= 0) intent.minPrice = obj.minPrice;
    if (typeof obj.maxPrice === 'number' && obj.maxPrice >= 0) intent.maxPrice = obj.maxPrice;

    if (obj.listingType && VALID_LISTING_TYPES.includes(obj.listingType)) intent.listingType = obj.listingType;
    else if (obj.listing_type && VALID_LISTING_TYPES.includes(obj.listing_type)) intent.listingType = obj.listing_type;

    if (obj.condition && VALID_CONDITIONS.includes(obj.condition)) intent.condition = obj.condition;
    else if (obj.condition === 'new' || obj.condition === 'New') intent.condition = 'New';
    else if (obj.condition === 'refurb' || obj.condition === 'Refurb') intent.condition = 'Refurb';
    else if (obj.condition === 'refurbished' || obj.condition === 'Refurbished') intent.condition = 'Refurb';
    else if (obj.condition === 'used' || obj.condition === 'Used') intent.condition = 'Used';

    if (obj.returnsAccepted === true || obj.returns_accepted === true) intent.returnsAccepted = true;

    if (obj.freeShipping === true || obj.free_shipping === true) intent.freeShipping = true;
    if (typeof obj.minQuantity === 'number' && obj.minQuantity >= 1) intent.minQuantity = Math.min(999, Math.max(1, obj.minQuantity));
    else if (typeof obj.min_quantity === 'number' && obj.min_quantity >= 1) intent.minQuantity = Math.min(999, Math.max(1, obj.min_quantity));
    if (obj.includeForParts === true || obj.include_for_parts === true) intent.includeForParts = true;

    if (obj.trustedLevel && VALID_TRUSTED_LEVELS.includes(obj.trustedLevel)) intent.trustedLevel = obj.trustedLevel;
    if (typeof obj.minSellerFeedbackPct === 'number') intent.minSellerFeedbackPct = obj.minSellerFeedbackPct;
    if (typeof obj.min_seller_feedback_pct === 'number') intent.minSellerFeedbackPct = obj.min_seller_feedback_pct;
    if (typeof obj.minSellerReviews === 'number') intent.minSellerReviews = obj.minSellerReviews;
    if (typeof obj.min_seller_reviews === 'number') intent.minSellerReviews = obj.min_seller_reviews;

    if (obj.sortOrder && VALID_SORT_ORDERS.includes(obj.sortOrder)) intent.sortOrder = obj.sortOrder;
    else if (obj.sort_order && VALID_SORT_ORDERS.includes(obj.sort_order)) intent.sortOrder = obj.sort_order;

    if (obj.categoryId && typeof obj.categoryId === 'string') intent.categoryId = obj.categoryId.trim();
    else if (obj.category_id && typeof obj.category_id === 'string') intent.categoryId = obj.category_id.trim();

    if (typeof obj.pageSize === 'number') intent.pageSize = Math.min(200, Math.max(1, obj.pageSize));
    else if (typeof obj.page_size === 'number') intent.pageSize = Math.min(200, Math.max(1, obj.page_size));

    return { intent };
  } catch (e) {
    return { intent: { ...DEFAULT_INTENT }, error: 'Invalid JSON' };
  }
}

/**
 * Parse markdown-style key: value lines.
 * @param {string} input
 * @returns {Partial<SearchIntent>}
 */
function parseMarkdownInput(input) {
  const intent = { ...DEFAULT_INTENT };
  const lines = input.split(/\r?\n/).map(l => l.trim()).filter(Boolean);

  for (const line of lines) {
    const m = line.match(/^([a-zA-Z_][a-zA-Z0-9_]*)\s*:\s*(.*)$/);
    if (!m) continue;
    const key = m[1].toLowerCase().replace(/_/g, '');
    const val = m[2].trim();

    if (key === 'q' || key === 'query') intent.query = val;
    else if (key === 'minprice' || key === 'min_price') intent.minPrice = parseFloat(val) || null;
    else if (key === 'maxprice' || key === 'max_price') intent.maxPrice = parseFloat(val) || null;
    else if (key === 'listingtype' || key === 'listing_type') {
      const v = val.toUpperCase().replace(/-/g, '_');
      if (VALID_LISTING_TYPES.includes(v)) intent.listingType = v;
    }
    else if (key === 'condition') {
      const v = val.toLowerCase();
      if (v === 'new') intent.condition = 'New';
      else if (v === 'refurb' || v === 'refurbished') intent.condition = 'Refurb';
      else if (v === 'used') intent.condition = 'Used';
      else if (v === 'all') intent.condition = 'all';
    }
    else if (key === 'returns' || key === 'returnsaccepted') intent.returnsAccepted = /^(1|true|yes)$/i.test(val);
    else if (key === 'shipping' || key === 'freeshipping') intent.freeShipping = /^(free|1|true|yes)$/i.test(val);
    else if (key === 'minquantity' || key === 'min_quantity' || key === 'qty') {
      const n = parseInt(val, 10);
      if (!isNaN(n) && n >= 1) intent.minQuantity = Math.min(999, n);
    }
    else if (key === 'forparts' || key === 'includeforparts' || key === 'include_for_parts') intent.includeForParts = /^(include|1|true|yes)$/i.test(val);
    else if (key === 'trusted' || key === 'trustedlevel') {
      if (VALID_TRUSTED_LEVELS.includes(val.toLowerCase())) intent.trustedLevel = val.toLowerCase();
    }
    else if (key === 'minsellerfeedbackpct' || key === 'min_feedback') intent.minSellerFeedbackPct = parseFloat(val) || null;
    else if (key === 'minsellerreviews' || key === 'min_reviews') intent.minSellerReviews = parseInt(val, 10) || null;
    else if (key === 'sort' || key === 'sortorder') {
      const v = val.replace(/-/g, '');
      if (VALID_SORT_ORDERS.includes(v)) intent.sortOrder = v;
      else if (SORT_ALIASES[v.toLowerCase()]) intent.sortOrder = SORT_ALIASES[v.toLowerCase()];
    }
    else if (key === 'category' || key === 'categoryid') intent.categoryId = val || null;
    else if (key === 'limit' || key === 'pagesize') intent.pageSize = Math.min(200, Math.max(1, parseInt(val, 10) || 20));
  }

  return intent;
}

/**
 * Parse input into normalized intent + warnings.
 * @param {string} input
 * @returns {ParseResult}
 */
export function parseInput(input) {
  const warnings = [];
  const mode = detectMode(input);

  if (mode === 'invalid') {
    return {
      intent: { ...DEFAULT_INTENT },
      mode: 'invalid',
      warnings: [],
      error: 'Invalid JSON'
    };
  }

  let intent = { ...DEFAULT_INTENT };

  if (mode === 'json') {
    const { intent: parsed, error } = parseJsonInput(input);
    if (error) {
      return { intent: { ...DEFAULT_INTENT }, mode: 'invalid', warnings: [], error };
    }
    intent = { ...intent, ...parsed };
  } else if (mode === 'markdown') {
    const parsed = parseMarkdownInput(input);
    intent = { ...intent, ...parsed };
  } else if (mode === 'shorthand') {
    const { keyword, filters, warnings: shorthandWarnings } = parseShorthand(input);
    intent = { ...intent, query: keyword, ...filters };
    warnings.push(...shorthandWarnings);
  } else {
    intent.query = (input || '').trim().replace(/\s+/g, ' ');
  }

  // Validation warnings
  if (intent.minPrice != null && (intent.minPrice < 0 || intent.minPrice > 100000)) {
    warnings.push({ code: 'invalid_range', message: 'Min price out of range', blocking: false });
    intent.minPrice = intent.minPrice < 0 ? 0 : 100000;
  }
  if (intent.maxPrice != null && (intent.maxPrice < 0 || intent.maxPrice > 100000)) {
    warnings.push({ code: 'invalid_range', message: 'Max price out of range', blocking: false });
    intent.maxPrice = intent.maxPrice < 0 ? 0 : 100000;
  }
  if (intent.minPrice != null && intent.maxPrice != null && intent.minPrice > intent.maxPrice) {
    warnings.push({ code: 'invalid_range', message: 'Min price exceeds max price', blocking: true });
  }
  if (intent.minSellerFeedbackPct != null && (intent.minSellerFeedbackPct < 0 || intent.minSellerFeedbackPct > 100)) {
    warnings.push({ code: 'invalid_range', message: 'Feedback % must be 0-100', blocking: false });
    intent.minSellerFeedbackPct = Math.max(0, Math.min(100, intent.minSellerFeedbackPct));
  }
  if (intent.minSellerReviews != null && intent.minSellerReviews < 0) {
    warnings.push({ code: 'invalid_range', message: 'Min reviews must be non-negative', blocking: false });
    intent.minSellerReviews = 0;
  }
  if (intent.minQuantity != null && (intent.minQuantity < 1 || intent.minQuantity > 999)) {
    warnings.push({ code: 'invalid_range', message: 'Min quantity must be 1–999', blocking: false });
    intent.minQuantity = Math.max(1, Math.min(999, intent.minQuantity || 1));
  }

  return { intent, mode, warnings, error: null };
}

/**
 * Convert intent to trusted-search API params (camelCase for api-service).
 * @param {SearchIntent} intent
 * @param {number} [page=1]
 * @returns {Object|null} Params for apiService.searchTrustedSellers, or null if no query
 */
export function intentToApiParams(intent, page = 1) {
  if (!intent || !intent.query || !intent.query.trim()) return null;

  const params = {
    q: intent.query.trim(),
    page,
    pageSize: intent.pageSize ?? 20
  };

  if (intent.minPrice != null && intent.minPrice > 0) params.minPrice = intent.minPrice;
  if (intent.maxPrice != null && intent.maxPrice < 10000) params.maxPrice = intent.maxPrice;
  if (intent.listingType) params.listingType = intent.listingType;
  if (intent.condition && intent.condition !== 'all') params.condition = intent.condition;
  if (intent.returnsAccepted) params.returnsAccepted = true;
  if (intent.freeShipping) params.freeShipping = true;
  params.minQuantity = intent.minQuantity ?? 1;
  if (intent.includeForParts) params.includeForParts = true;
  if (intent.categoryId) params.categoryId = intent.categoryId;
  if (intent.sortOrder) params.sortOrder = intent.sortOrder;

  /*
   * With the floor off, the seller filters are switched off explicitly rather
   * than left out or sent as zero, and the difference is not cosmetic.
   *
   *   * Omitting them makes the endpoint substitute its own 99.5 / 50 default,
   *     so "Off" quietly ran a Strict search under a control that said Off.
   *   * Sending 0 / 0 keeps a floor nominally in force, and a floor in force
   *     also drops every listing whose seller numbers the search response left
   *     out -- which on this data is most of them. Off would still have hidden
   *     the unrated sellers, the one thing Off promises not to do.
   *
   * `skip_feedback` / `skip_reviews` are the endpoint's own way of saying "no
   * seller floor at all", and they are what Off means.
   */
  const preset = TRUSTED_PRESETS[intent.trustedLevel] || TRUSTED_PRESETS.strict;
  if (intent.trustedLevel === 'off') {
    params.skipFeedback = true;
    params.skipReviews = true;
  } else {
    params.minSellerFeedbackPct = intent.minSellerFeedbackPct ?? preset.feedback;
    params.minSellerReviews = intent.minSellerReviews ?? preset.reviews;
  }

  return params;
}

/**
 * Build display filters for pills (non-default only).
 * @param {SearchIntent} intent
 * @returns {Array<{key: string, label: string, value: string}>}
 */
export function intentToDisplayFilters(intent) {
  if (!intent) return [];
  const pills = [];

  if (intent.minPrice != null && intent.minPrice > 0) {
    pills.push({ key: 'minPrice', label: 'Min price', value: `$${intent.minPrice}` });
  }
  if (intent.maxPrice != null && intent.maxPrice < 10000) {
    pills.push({ key: 'maxPrice', label: 'Max price', value: `$${intent.maxPrice}` });
  }
  if (intent.listingType && intent.listingType !== 'FIXED_PRICE') {
    pills.push({ key: 'listingType', label: 'Listing', value: intent.listingType === 'BOTH' ? 'Both' : 'Auction' });
  }
  if (intent.condition && intent.condition !== 'all') {
    pills.push({ key: 'condition', label: 'Condition', value: intent.condition });
  }
  if (intent.returnsAccepted) {
    pills.push({ key: 'returnsAccepted', label: 'Returns', value: 'Yes' });
  }
  if (intent.freeShipping) {
    pills.push({ key: 'freeShipping', label: 'Free shipping', value: 'Yes' });
  }
  if (intent.minQuantity != null && intent.minQuantity > 1) {
    pills.push({ key: 'minQuantity', label: 'Min qty', value: String(intent.minQuantity) });
  }
  if (intent.includeForParts) {
    pills.push({ key: 'includeForParts', label: 'For Parts', value: 'Include' });
  }
  if (intent.trustedLevel && intent.trustedLevel !== 'strict') {
    pills.push({ key: 'trustedLevel', label: 'Trust', value: intent.trustedLevel });
  }
  if (intent.sortOrder && intent.sortOrder !== 'priceAscending') {
    const labels = { priceDescending: 'Price desc', sellerScoreDescending: 'Seller score', newlyListed: 'Newest', bestMatch: 'Relevance' };
    pills.push({ key: 'sortOrder', label: 'Sort', value: labels[intent.sortOrder] || intent.sortOrder });
  }
  if (intent.categoryId) {
    pills.push({ key: 'categoryId', label: 'Category', value: intent.categoryId });
  }
  if (intent.pageSize && intent.pageSize !== 20) {
    pills.push({ key: 'pageSize', label: 'Limit', value: String(intent.pageSize) });
  }

  return pills;
}

/**
 * Regenerate canonical input string from intent (for pill removal / state sync).
 * @param {SearchIntent} intent
 * @returns {string}
 */
export function intentToCanonicalInput(intent) {
  if (!intent) return '';
  const parts = [];

  const q = (intent.query || '').trim();
  if (q) parts.push(q);

  if (intent.minPrice != null && intent.minPrice > 0 && intent.maxPrice != null && intent.maxPrice < 10000) {
    parts.push(`$${intent.minPrice}-$${intent.maxPrice}`);
  } else if (intent.minPrice != null && intent.minPrice > 0) {
    parts.push(`$${intent.minPrice}`);
  } else if (intent.maxPrice != null && intent.maxPrice < 10000) {
    parts.push(`<$${intent.maxPrice}`);
  }

  if (intent.condition && intent.condition !== 'all') parts.push(`condition:${intent.condition.toLowerCase()}`);
  if (intent.listingType && intent.listingType !== 'FIXED_PRICE') {
    const v = intent.listingType === 'BOTH' ? 'both' : intent.listingType === 'AUCTION' ? 'auction' : 'fixed';
    parts.push(`listing:${v}`);
  }
  if (intent.sortOrder && intent.sortOrder !== 'priceAscending') {
    const rev = Object.entries(SORT_ALIASES).find(([, val]) => val === intent.sortOrder);
    parts.push(`sort:${rev ? rev[0] : intent.sortOrder}`);
  }
  if (intent.trustedLevel && intent.trustedLevel !== 'strict') parts.push(`trusted:${intent.trustedLevel}`);
  if (intent.returnsAccepted) parts.push('returns:yes');
  if (intent.freeShipping) parts.push('shipping:free');
  if (intent.minQuantity != null && intent.minQuantity > 1) parts.push(`qty:${intent.minQuantity}`);
  if (intent.includeForParts) parts.push('forparts:include');
  if (intent.categoryId) parts.push(`category:${intent.categoryId}`);
  if (intent.pageSize && intent.pageSize !== 20) parts.push(`limit:${intent.pageSize}`);

  return parts.filter(Boolean).join(' ');
}

export { DEFAULT_INTENT, VALID_SORT_ORDERS, VALID_LISTING_TYPES, VALID_CONDITIONS, VALID_TRUSTED_LEVELS, TRUSTED_PRESETS, SYNTAX_HELP, EXAMPLES };
